import { useCallback } from 'react';
import { StorePerformanceInterval, createStorePerformanceRequest } from '../../../constants';
import {
  ConversionRateOverviewResponse,
  MemberOverviewResponse,
  QueueCustomerOverviewResponse,
  RfidOverviewResponse,
  SalesOverviewResponse,
  useAsyncAction,
  useRfid,
  useStoreInformation,
  useTransaction,
} from '../../../hooks';
import { isFailureResponse } from '../../../utils';

export const useFetchAreaWidgetsData = ({
  interval,
  currentDate,
}: {
  interval: StorePerformanceInterval;
  currentDate: Date;
}) => {
  const { currencyCode } = useStoreInformation();

  const {
    fetchQueueCustomerOverviewData,
    fetchMembersOverviewData,
    fetchConversionRateOverviewData,
    fetchSalesOverviewData,
  } = useTransaction();

  const { fetchRfidOverviewData } = useRfid();

  // Rfid Overview Widget
  const {
    result: rfidOverviewData,
    isLoading: rfidOverviewLoading,
    isError: rfidOverviewError,
    triggerAction: triggerFetchRfidOverview,
  } = useAsyncAction<RfidOverviewResponse>();

  const callFetchRfidOverviewData = async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });
    const response = await fetchRfidOverviewData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  };

  const fetchRfidOverviewWidgetData = () => {
    triggerFetchRfidOverview(callFetchRfidOverviewData);
  };

  // Queue Customer Overview Widget
  const {
    result: queueCustomerOverviewData,
    isLoading: queueCustomerOverviewLoading,
    isError: queueCustomerOverviewError,
    triggerAction: triggerFetchQueueCustomerOverview,
  } = useAsyncAction<QueueCustomerOverviewResponse>();

  const callFetchCustomerOverviewData = async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });
    const response = await fetchQueueCustomerOverviewData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  };

  const fetchQueueCustomerOverviewWidgetData = () => {
    triggerFetchQueueCustomerOverview(callFetchCustomerOverviewData);
  };

  // Member Overview Widget
  const {
    result: membersOverviewData,
    isLoading: membersOverviewLoading,
    isError: membersOverviewError,
    triggerAction: triggerFetchMembersOverview,
  } = useAsyncAction<MemberOverviewResponse>();

  const callFetchMemberOverviewData = async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });
    const response = await fetchMembersOverviewData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  };

  const fetchMemberOverviewWidgetData = () => {
    triggerFetchMembersOverview(callFetchMemberOverviewData);
  };

  // Sales Overview Widget
  const {
    result: salesOverviewData,
    isLoading: salesOverviewLoading,
    isError: salesOverviewError,
    triggerAction: triggerFetchSalesOverview,
  } = useAsyncAction<SalesOverviewResponse>();

  const callFetchSalesOverviewData = useCallback(async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });
    const response = await fetchSalesOverviewData(request, currencyCode);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  }, [currentDate, currencyCode]);

  const fetchSalesOverviewWidgetData = () => {
    triggerFetchSalesOverview(callFetchSalesOverviewData);
  };

  // Conversion Rate Overview Widget
  const {
    result: conversionRateOverviewData,
    isLoading: conversionRateOverviewLoading,
    isError: conversionRateOverviewError,
    triggerAction: triggerFetchConversionRateOverview,
  } = useAsyncAction<ConversionRateOverviewResponse>();

  const callFetchConversionRateOverviewData = async () => {
    const request = createStorePerformanceRequest({
      interval,
      currentDate,
    });
    const response = await fetchConversionRateOverviewData(request);

    if (isFailureResponse(response)) {
      throw new Error();
    }

    return response.data;
  };

  const fetchConversionRateOverviewWidgetData = () => {
    triggerFetchConversionRateOverview(callFetchConversionRateOverviewData);
  };

  return {
    conversionRateOverviewData,
    conversionRateOverviewLoading,
    conversionRateOverviewError,
    fetchConversionRateOverviewWidgetData,

    salesOverviewData,
    salesOverviewLoading,
    salesOverviewError,
    fetchSalesOverviewWidgetData,

    membersOverviewData,
    membersOverviewLoading,
    membersOverviewError,
    fetchMemberOverviewWidgetData,

    queueCustomerOverviewData,
    queueCustomerOverviewLoading,
    queueCustomerOverviewError,
    fetchQueueCustomerOverviewWidgetData,

    rfidOverviewData,
    rfidOverviewLoading,
    rfidOverviewError,
    fetchRfidOverviewWidgetData,
  };
};
